import { FunctionComponent, memo, useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { SecureTextInputFieldC } from "../../Components/SecureTextInput";
import { useAppDispatch, useAppSelector } from "../../Redux/Store/Hooks";
import Select from "../../Components/Select/AutoComplete";
import { AutocompleteOption } from "../../Components/Select/ISelectProps";
import TextAreaInputField from "../../Components/TextAreaInput/TextAreaInputField";
import NumberInputField from "../../Components/NumberInput/NumberInputField";
import { EditUser } from "../../Redux/Slices/User/EditUserSlice";
import { addUser } from "../../Redux/Slices/User/AddUserSlice";
import { IUserAdmin } from "../UserAdmin/IUserAdmin";
import { EditUserAdmin } from "../../Redux/Slices/UserAdmin/EditUserAdminSlice";
import { addUserAdmin } from "../../Redux/Slices/UserAdmin/AddUserAdminSlice";
import { registrationStart } from "../../Redux/Slices/Registration/RegistrationSlicer";

interface UserFormProps {
  formRef: React.RefObject<HTMLFormElement>;
  initialValues: IUserAdmin;
  setInitialValues: React.Dispatch<React.SetStateAction<IUserAdmin>>;
}

const UserForm: FunctionComponent<UserFormProps> = (props) => {
  const dispatch = useAppDispatch();

  const { data: listRole } = useAppSelector((state) => state.ListRoleSlice);

  const ListRole = Array.isArray(listRole?.data)
    ? listRole?.data.map((listRole) => ({
      label: listRole.name!,
      value: listRole.id,
    }))
    : [];

  const userSchema = z.object({
    id: z.number().optional(),
    username: z
      .string({ message: "Please enter name" })
      .min(1, { message: "Please enter name" })
      .regex(
        /^[a-zA-Z0-9_\s]+$/,
        "Name can only contain letters, numbers, underscores and spaces"
      ),
    email: z
      .string({
        required_error: "Please enter a valid email ID",
        message: "Please enter a valid email ID",
      })
      .email({
        message: "Please enter a valid email ID",
      }),
    parent_email: z
      .string({
        required_error: "Please enter a valid parent email ID",
        message: "Please enter a valid parent email ID",
      })
      .email({
        message: "Please enter a valid parent email ID",
      }),
    // year_level: z.string({ message: "Please select year group" }),
    yeargroup_country_map_id: z.number({ message: "Please select year group" }),

    role: z.number({ message: "Please select role" }),
    phone_no: z
      .any()
      .optional()
      .refine((val) => !val || (String(val).length >= 7 && String(val).length <= 15), {
        message: "Phone number must be between 7 and 15 digits",
      }),

    status: z.boolean({
      message: "Please select the status",
    }),
    password: z
      .string()
      .optional()
      .or(z.literal("")),
    confirm_password: z
      .string()
      .optional()
      .or(z.literal("")),
  }).refine((data) => !data.password || data.password === data.confirm_password, {
    message: "Passwords does not match",
    path: ["confirm_password"],
  });

  const methods = useForm<IUserAdmin>({
    defaultValues: {
      ...props.initialValues,
      status: props.initialValues.id ? props.initialValues.status : true,
      phone_no:
        props.initialValues?.phone_no != null &&
          props.initialValues?.phone_no !== ""
          ? Number(props.initialValues.phone_no)
          : undefined,
    },
    resolver: zodResolver(userSchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<IUserAdmin> = (val) => {
    const payload = {
      ...val,
      phone_no: String(val.phone_no || ""),
      status: val.status ?? true,
      role: val.role ?? 0,
      yeargroup_country_map_id: Number(val.yeargroup_country_map_id),
    };

    if (val.id) {
      dispatch(EditUserAdmin({ ...payload, id: val.id }));
    } else {
      dispatch(registrationStart([payload]));
    }
  };

  const { data: listYearlevel_maps } = useAppSelector(
    (state) => state.ListYearlevel_mapSlice
  );

  const ListYearLevel = Array.isArray(listYearlevel_maps?.data)
    ? listYearlevel_maps?.data?.map((listQuestions) => ({
      label:
        `${listQuestions?.yeargroup?.name}-${listQuestions?.region?.name}-${listQuestions?.region?.state}`!,
      value: listQuestions.id,
    }))
    : [];

  useEffect(() => {
    return () => {
      props.setInitialValues({});
    };
  }, [dispatch, props]);

  // Status list
  const RoleSatus = [
    {
      label: "Active",
      value: true,
    },

    {
      label: "Inactive",
      value: false,
    },
  ];

  const ListYearSliceLoading = useAppSelector(
    (state) => state.ListYearGroupSlice
  );
  const ListYear = ListYearSliceLoading?.data;

  const YearList = Array.isArray(ListYear?.data)
    ? ListYear?.data?.map((listExams) => ({
      label: listExams?.year_group!,
      value: listExams?.year_group,
    }))
    : [];
  return (
    <FormProvider {...methods}>
      <form
        ref={props.formRef}
        onSubmit={methods.handleSubmit(onSubmit)}
        className="form_double_input"
      >
        <SecureTextInputFieldC name="username" label="Name" type="text" emojiErrorMessage="Emojis are not allowed in usernames" />
        <SecureTextInputFieldC name="email" label="Email" type="email" />
        <SecureTextInputFieldC name="parent_email" label="Parent Email" type="email" />
        {/* <Select
          name="year_level"
          label="Year Level"
          options={YearList ?? []}
          value={
            YearList?.find(
              (list) => list.value === methods.getValues("year_level")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("year_level", val?.value, {
              shouldValidate: true,
            });
          }}
        /> */}

        <Select
          name="yeargroup_country_map_id"
          label="Select the Year group "
          options={ListYearLevel ?? []}
          value={
            ListYearLevel?.find(
              (list) =>
                list.value === methods.getValues("yeargroup_country_map_id")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("yeargroup_country_map_id", val?.value, {
              shouldValidate: true,
            });
          }}
        />

        <NumberInputField
          name="phone_no"
          label="Phone Number"
          type="number"
          disabledScrollBar
        />

        <Select
          name="role"
          label="Role"
          options={ListRole ?? []}
          value={
            ListRole?.find(
              (list) => list.value === methods.getValues("role")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("role", val?.value, {
              shouldValidate: true,
            });
          }}
        />
        <Select
          name="status"
          label="Status"
          options={RoleSatus}
          value={
            RoleSatus?.find(
              (role) => role.value === methods.getValues("status")
            ) ?? null
          }
          onChange={(_, value) => {
            const val = value as AutocompleteOption;
            methods.setValue("status", val?.value, {
              shouldValidate: true,
            });
          }}
        />
      </form>
    </FormProvider>
  );
};

export default memo(UserForm);
